import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  History,
  Inbox,
  Loader2,
  Mail,
  MailOpen,
  Play,
  RefreshCw,
  Send,
  TriangleAlert,
  Users,
  X,
} from 'lucide-react';
import type {
  EmailSend,
  EmailSendCounts,
  EmailSendRecipient,
  EmailSendStatus,
} from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';

// ─────────────────────────────────────────────────────────────────
// Send history — the master list, with one send's detail beside it
//
// A *send* is one queueing of an email to an audience, and that is the unit
// this page is organised around: the list on the left is one row per send, and
// the panel on the right is everything that send knows — who it went out as,
// what it was targeted at, its timeline, its delivery tallies, and its
// recipients, filterable by what happened to each one.
//
// There is no extra endpoint behind the panel: the row already carries the
// targets and tallies (GET /api/email-sends), the selected send is re-read
// while it is still in flight so its numbers tick up, and recipients are paged
// in only while somebody is looking at them.
//
// The one thing a recipient row cannot carry is the copy it was sent — a whole
// rendered email, per recipient — so that is the one request made per click:
// GET /api/email-sends/:id/recipients/:recipientId/content fetches the snapshot
// the sender job kept, and it is shown exactly as it went out.
// ─────────────────────────────────────────────────────────────────

const STATUS_META: Record<EmailSendStatus, { label: string; className: string }> = {
  queued: { label: 'Queued', className: 'badge-scheduled' },
  sending: { label: 'Sending', className: 'badge-sending' },
  sent: { label: 'Sent', className: 'badge-sent' },
  failed: { label: 'Failed', className: 'badge-failed' },
  cancelled: { label: 'Cancelled', className: 'badge-archived' },
};

const RECIPIENT_STATUSES = ['queued', 'delivered', 'bounced', 'complained', 'failed'] as const;
type RecipientStatus = (typeof RECIPIENT_STATUSES)[number];

const RECIPIENT_META: Record<RecipientStatus, { label: string; className: string }> = {
  queued: { label: 'Queued', className: 'badge-scheduled' },
  delivered: { label: 'Delivered', className: 'badge-sent' },
  bounced: { label: 'Bounced', className: 'badge-failed' },
  complained: { label: 'Complained', className: 'badge-failed' },
  failed: { label: 'Failed', className: 'badge-failed' },
};

const PAGE_SIZE = 20;

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "Ada <ada@example.com>" — the From header a recipient saw. */
function fromHeader(send: EmailSend): string {
  const name = send.fromName?.trim();
  return name ? `${name} <${send.fromAddress}>` : send.fromAddress;
}

/** Whether a send is still moving — drives polling and the cancel affordance. */
function isLive(status: EmailSendStatus): boolean {
  return status === 'queued' || status === 'sending';
}

export function EmailHistoryPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const sendsQuery = useQuery({
    queryKey: ['email-sends', { page, limit: PAGE_SIZE }],
    queryFn: () => api.emailSends.list({ page, limit: PAGE_SIZE }),
    enabled: Boolean(activeWorkspaceId),
    // A history page that is watching a send should watch it: the list keeps
    // being re-read while anything on it is still queued or sending.
    refetchInterval: (query) =>
      (query.state.data?.data ?? []).some((send) => isLive(send.status)) ? 8_000 : false,
  });

  const sends = sendsQuery.data?.data ?? [];
  const total = sendsQuery.data?.total ?? 0;
  const totalPages = sendsQuery.data?.totalPages ?? 1;

  // The row is the detail — it carries the targets and the tallies — but the
  // selected send is re-read so a live one ticks up, and so a change made
  // elsewhere is reflected instead of frozen.
  const detailQuery = useQuery({
    queryKey: ['email-sends', 'detail', selectedId],
    queryFn: () => api.emailSends.get(selectedId!),
    enabled: Boolean(selectedId),
    refetchInterval: (query) =>
      query.state.data && isLive(query.state.data.status) ? 5_000 : false,
  });

  const selected = detailQuery.data ?? sends.find((send) => send.id === selectedId) ?? null;

  const workspaces = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces.list(),
    enabled: Boolean(activeWorkspaceId),
  });
  const role =
    workspaces.data?.workspaces.find((w) => w.id === activeWorkspaceId)?.role ?? 'member';
  const canManage = role === 'owner' || role === 'admin';

  const cancelSend = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) => api.emailSends.cancel(id, force),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['email-sends'] }),
  });

  const resumeSend = useMutation({
    mutationFn: (id: string) => api.emailSends.resume(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['email-sends'] }),
  });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="shrink-0 px-8 pt-8 pb-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="kicker mb-2">
              <span className="bg-accent h-1.5 w-1.5 rounded-full" />
              Outreach
            </p>
            <h1 className="text-ink text-[22px] font-bold tracking-tight">Send history</h1>
            <p className="text-ink-soft mt-1 max-w-2xl text-[13px]">
              Every send, newest first. Pick one to see who it went to, how it was addressed, and
              what happened to each recipient.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void sendsQuery.refetch()}
            disabled={sendsQuery.isFetching}
            className="btn-ghost px-3 py-2 text-[12.5px]"
          >
            {sendsQuery.isFetching ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <RefreshCw size={13} />
            )}
            Refresh
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 px-8 pb-8">
        <div className="grid h-full min-h-0 gap-5 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
          {/* ── Master: one row per send ─────────────────────── */}
          <section
            className={`surface-row flex min-h-0 flex-col overflow-hidden ${
              selected ? 'hidden lg:flex' : 'flex'
            }`}
          >
            <div className="border-line flex items-center gap-2 border-b px-4 py-3">
              <History size={14} className="text-accent" />
              <h2 className="text-ink text-[13px] font-bold">Sends</h2>
              <span className="text-muted ml-auto text-[11.5px]">
                {total.toLocaleString()} total
              </span>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {sendsQuery.isLoading ? (
                <p className="text-muted flex items-center gap-2 px-4 py-6 text-xs">
                  <Loader2 size={13} className="animate-spin" />
                  Loading sends…
                </p>
              ) : sendsQuery.isError ? (
                <p className="text-danger px-4 py-6 text-xs">
                  {sendsQuery.error instanceof Error
                    ? sendsQuery.error.message
                    : 'Could not load sends.'}
                </p>
              ) : sends.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <Inbox size={20} className="text-muted mx-auto mb-2" />
                  <p className="text-ink text-[13px] font-semibold">No sends yet</p>
                  <p className="text-muted mt-1 text-[12px]">
                    Queue one from the Send page — to a list, or to an uploaded CSV.
                  </p>
                </div>
              ) : (
                sends.map((send) => (
                  <SendRow
                    key={send.id}
                    send={send}
                    selected={send.id === selectedId}
                    onSelect={() => setSelectedId(send.id)}
                  />
                ))
              )}
            </div>

            {totalPages > 1 && (
              <div className="border-line flex items-center justify-between border-t px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="btn-ghost px-2 py-1 text-[12px] disabled:opacity-40"
                >
                  <ChevronLeft size={13} />
                  Newer
                </button>
                <span className="text-muted text-[11.5px]">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="btn-ghost px-2 py-1 text-[12px] disabled:opacity-40"
                >
                  Older
                  <ChevronRight size={13} />
                </button>
              </div>
            )}
          </section>

          {/* ── Detail: the selected send ────────────────────── */}
          <section className={`min-h-0 overflow-hidden ${selected ? 'block' : 'hidden lg:block'}`}>
            {selected ? (
              <SendDetail
                send={selected}
                canManage={canManage}
                onBack={() => setSelectedId(null)}
                onCancel={(force) => cancelSend.mutate({ id: selected.id, force })}
                cancelling={cancelSend.isPending}
                onResume={() => resumeSend.mutate(selected.id)}
                resuming={resumeSend.isPending}
              />
            ) : (
              <div className="surface-row flex h-full min-h-64 flex-col items-center justify-center gap-2 p-8 text-center">
                <Mail size={20} className="text-muted" />
                <p className="text-ink text-[13px] font-semibold">Pick a send</p>
                <p className="text-muted max-w-sm text-[12px]">
                  Its audience, timeline, delivery counts and recipients appear here.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Master list row
//
// Exported alongside the detail panel and the tallies so their markup can be
// pinned by a static-render test (see EmailHistoryPage.test.tsx): the page
// itself needs a router and a session, the pieces need only a send.
// ─────────────────────────────────────────────────────────────────
export function SendRow({
  send,
  selected,
  onSelect,
}: {
  send: EmailSend;
  selected: boolean;
  onSelect: () => void;
}) {
  const meta = STATUS_META[send.status];
  const counts = send.recipients;
  const settled = counts.delivered + counts.bounced + counts.complained + counts.failed;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`border-line w-full border-b px-4 py-3 text-left transition-colors last:border-b-0 ${
        selected ? 'bg-accent/8' : 'hover:bg-ink/[0.03]'
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="text-ink min-w-0 flex-1 truncate text-[13px] font-semibold">
          {send.emailSubject}
        </span>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${meta.className}`}
        >
          {meta.label}
        </span>
      </div>

      <div className="text-muted mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
        <span>
          {counts.total.toLocaleString()} recipient{counts.total === 1 ? '' : 's'}
        </span>
        <span aria-hidden="true">·</span>
        <span>{formatWhen(send.createdAt)}</span>
        {send.csvFilename && (
          <>
            <span aria-hidden="true">·</span>
            <span className="text-ink-soft inline-flex items-center gap-1">
              <FileSpreadsheet size={11} />
              CSV
            </span>
          </>
        )}
      </div>

      {counts.total > 0 && (
        <div className="bg-ink/5 mt-2 h-1 overflow-hidden rounded-full" aria-hidden="true">
          <div
            className="bg-accent h-full rounded-full transition-all"
            style={{ width: `${Math.round((settled / counts.total) * 100)}%` }}
          />
        </div>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Detail panel
// ─────────────────────────────────────────────────────────────────
export function SendDetail({
  send,
  canManage,
  onBack,
  onCancel,
  cancelling,
  onResume,
  resuming,
}: {
  send: EmailSend;
  canManage: boolean;
  onBack: () => void;
  onCancel: (force: boolean) => void;
  cancelling: boolean;
  onResume: () => void;
  resuming: boolean;
}) {
  const meta = STATUS_META[send.status];
  const canCancel = canManage && isLive(send.status) && !send.cancelledAt;
  // A stopped send can be put back in the queue: cancelling never removed the
  // recipients, so resuming delivers the ones still waiting and leaves the
  // delivered ones alone.
  const canResume = canManage && send.status === 'cancelled' && !send.completedAt;

  return (
    <div className="surface-row flex h-full min-h-0 flex-col overflow-hidden">
      <div className="border-line flex items-start gap-3 border-b px-5 py-4">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to the send list"
          className="btn-ghost h-8 w-8 shrink-0 rounded-lg px-0 lg:hidden"
        >
          <ArrowLeft size={15} />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-ink min-w-0 truncate text-[15px] font-bold tracking-tight">
              {send.emailSubject}
            </h2>
            <span
              className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${meta.className}`}
            >
              {meta.label}
            </span>
          </div>
          <p className="text-muted mt-0.5 text-[12px]">
            {send.recipients.total.toLocaleString()} recipient
            {send.recipients.total === 1 ? '' : 's'} · queued {formatWhen(send.createdAt)}
            {send.senderName ? ` · via ${send.senderName}` : ''}
          </p>
        </div>

        {canCancel && (
          <button
            type="button"
            onClick={() => onCancel(send.status === 'sending')}
            disabled={cancelling}
            className="btn-ghost text-danger shrink-0 px-2.5 py-1.5 text-[12px]"
            title={
              send.status === 'sending'
                ? 'Stop a send that has already started'
                : 'Call this send off before it starts'
            }
          >
            {cancelling ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />}
            {send.status === 'sending' ? 'Stop now' : 'Cancel send'}
          </button>
        )}

        {canResume && (
          <button
            type="button"
            onClick={onResume}
            disabled={resuming}
            className="btn-ghost shrink-0 px-2.5 py-1.5 text-[12px]"
            title={
              send.recipients.queued > 0
                ? `Deliver the ${send.recipients.queued.toLocaleString()} recipient${
                    send.recipients.queued === 1 ? '' : 's'
                  } this send never got to`
                : 'Put this send back in the queue'
            }
          >
            {resuming ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            Resume send
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
        <DeliveryCounts counts={send.recipients} />

        <section>
          <h3 className="field-label">How it went out</h3>
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Detail label="From" value={fromHeader(send)} mono />
            <Detail label="Reply-to" value={send.replyTo ?? '—'} mono />
            <Detail
              label="Audience"
              value={
                send.csvFilename
                  ? `CSV upload — ${send.csvFilename}`
                  : send.targets.length > 0
                    ? send.targets.map((target) => target.name ?? target.id).join(', ')
                    : '—'
              }
            />
            <Detail label="Sender" value={send.senderName ?? '—'} />
          </dl>
          {send.csvFilename && (
            <p className="text-muted mt-2 text-[11px]">
              The uploaded audience was temporary: it was deleted when this send finished, so the
              recipients above are the record of who it reached.
            </p>
          )}
        </section>

        <section>
          <h3 className="field-label">Timeline</h3>
          <ol className="space-y-1.5">
            <TimelineRow icon={History} label="Queued" at={send.createdAt} />
            {send.scheduledAt && (
              <TimelineRow icon={Clock} label="Scheduled for" at={send.scheduledAt} />
            )}
            <TimelineRow icon={Send} label="Started sending" at={send.startedAt} />
            {send.cancelledAt ? (
              <TimelineRow icon={Ban} label="Cancelled" at={send.cancelledAt} tone="danger" />
            ) : (
              <TimelineRow icon={CheckCircle2} label="Finished" at={send.completedAt} />
            )}
          </ol>
        </section>

        <RecipientsTable send={send} />
      </div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted text-[11px] tracking-wide uppercase">{label}</dt>
      <dd
        className={`text-ink-soft truncate text-[12.5px] ${mono ? 'font-mono' : ''}`}
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

function TimelineRow({
  icon: Icon,
  label,
  at,
  tone,
}: {
  icon: typeof History;
  label: string;
  at: string | null;
  tone?: 'danger';
}) {
  return (
    <li className="flex items-center gap-2.5 text-[12.5px]">
      <Icon
        size={13}
        className={at ? (tone === 'danger' ? 'text-danger' : 'text-accent') : 'text-muted'}
      />
      <span className={at ? 'text-ink-soft' : 'text-muted'}>{label}</span>
      <span className={`ml-auto ${at ? 'text-ink' : 'text-muted'}`}>{formatWhen(at)}</span>
    </li>
  );
}

/** The six delivery tallies, as tiles plus one bar. */
export function DeliveryCounts({ counts }: { counts: EmailSendCounts }) {
  const tiles: { label: string; value: number; tone: string }[] = [
    { label: 'Total', value: counts.total, tone: 'text-ink' },
    { label: 'Queued', value: counts.queued, tone: 'text-ink-soft' },
    { label: 'Delivered', value: counts.delivered, tone: 'text-ok' },
    { label: 'Bounced', value: counts.bounced, tone: 'text-danger' },
    { label: 'Complained', value: counts.complained, tone: 'text-danger' },
    { label: 'Failed', value: counts.failed, tone: 'text-danger' },
  ];

  const settled = counts.delivered + counts.bounced + counts.complained + counts.failed;

  return (
    <section>
      <h3 className="field-label">Delivery</h3>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {tiles.map((tile) => (
          <div key={tile.label} className="border-line bg-bg/60 rounded-xl border px-2.5 py-2">
            <p className={`text-[17px] leading-none font-bold tracking-tight ${tile.tone}`}>
              {tile.value.toLocaleString()}
            </p>
            <p className="text-muted mt-1 text-[10.5px] tracking-wide uppercase">{tile.label}</p>
          </div>
        ))}
      </div>

      {counts.total > 0 && (
        <div className="bg-ink/5 mt-3 flex h-1.5 overflow-hidden rounded-full" aria-hidden="true">
          <div
            className="bg-ok h-full"
            style={{ width: `${(counts.delivered / counts.total) * 100}%` }}
          />
          <div
            className="bg-danger h-full"
            style={{
              width: `${((counts.bounced + counts.complained + counts.failed) / counts.total) * 100}%`,
            }}
          />
        </div>
      )}
      <p className="text-muted mt-2 text-[11px]">
        {settled.toLocaleString()} of {counts.total.toLocaleString()} recipient
        {counts.total === 1 ? '' : 's'} settled — the rest are still queued for delivery.
      </p>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Recipients — paged in only while a send is being looked at
// ─────────────────────────────────────────────────────────────────
function RecipientsTable({ send }: { send: EmailSend }) {
  const [status, setStatus] = useState<RecipientStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  /** The recipient whose sent copy is open, if any. */
  const [opened, setOpened] = useState<EmailSendRecipient | null>(null);

  // A different send (or filter) is a different list: start at the top.
  useEffect(() => setPage(1), [send.id, status]);

  const recipientsQuery = useQuery({
    queryKey: ['email-sends', send.id, 'recipients', { page, status }],
    queryFn: () =>
      api.emailSends.recipients(send.id, {
        page,
        limit: 25,
        ...(status === 'all' ? {} : { status }),
      }),
    enabled: send.recipients.total > 0,
    staleTime: 10_000,
  });

  const rows: EmailSendRecipient[] = recipientsQuery.data?.data ?? [];
  const totalPages = recipientsQuery.data?.totalPages ?? 1;
  const filtered = recipientsQuery.data?.total ?? 0;

  const chips = useMemo(
    () =>
      [
        { id: 'all' as const, label: 'All', count: send.recipients.total },
        ...RECIPIENT_STATUSES.map((entry) => ({
          id: entry,
          label: RECIPIENT_META[entry].label,
          count: send.recipients[entry],
        })),
      ].filter((chip) => chip.id === 'all' || chip.count > 0),
    [send.recipients],
  );

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="field-label mb-0">Recipients</h3>
        <div className="border-line bg-bg/60 ml-auto flex flex-wrap items-center gap-1 rounded-full border p-1">
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={() => setStatus(chip.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all ${
                status === chip.id ? 'bg-card text-ink shadow-sm' : 'text-muted hover:text-ink-soft'
              }`}
            >
              {chip.label}
              <span className="text-muted ml-1 font-normal">{chip.count.toLocaleString()}</span>
            </button>
          ))}
        </div>
      </div>

      {send.recipients.total === 0 ? (
        <p className="text-muted text-[12px]">
          The snapshot found nobody to send to — an audience with no reachable addresses, since
          contacts who opted out are left out before queueing.
        </p>
      ) : recipientsQuery.isLoading ? (
        <p className="text-muted flex items-center gap-2 text-xs">
          <Loader2 size={13} className="animate-spin" />
          Loading recipients…
        </p>
      ) : recipientsQuery.isError ? (
        <p className="text-danger text-xs">
          {recipientsQuery.error instanceof Error
            ? recipientsQuery.error.message
            : 'Could not load recipients.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted text-[12px]">No recipients with that status.</p>
      ) : (
        <>
          <div className="border-line overflow-hidden rounded-xl border">
            <table className="w-full text-left">
              <thead className="bg-bg/60">
                <tr className="text-muted text-[10.5px] tracking-wide uppercase">
                  <th className="px-3 py-2 font-semibold">Recipient</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="hidden px-3 py-2 font-semibold sm:table-cell">Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((recipient) => (
                  <RecipientRow key={recipient.id} recipient={recipient} onOpen={setOpened} />
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-2 flex items-center justify-between">
              <span className="text-muted text-[11.5px]">
                {filtered.toLocaleString()} in this view
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="btn-ghost px-2 py-1 text-[12px] disabled:opacity-40"
                >
                  <ChevronLeft size={13} />
                  Prev
                </button>
                <span className="text-muted text-[11.5px]">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="btn-ghost px-2 py-1 text-[12px] disabled:opacity-40"
                >
                  Next
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <p className="text-muted mt-2 flex items-center gap-1.5 text-[11px]">
        <Users size={11} />A send's recipients are frozen when it is queued: editing a list
        afterwards does not change this. A delivered address opens the copy that recipient was
        actually sent.
      </p>

      {opened && (
        <RecipientCopyModal
          sendId={send.id}
          subject={send.emailSubject}
          recipient={opened}
          onClose={() => setOpened(null)}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// One recipient row
//
// Exported so the rule that decides whether a sent copy can be opened is pinned
// by a static-render test: only a recipient the sender job kept a copy for
// offers it, and everyone else is plain text.
// ─────────────────────────────────────────────────────────────────
export function RecipientRow({
  recipient,
  onOpen,
}: {
  recipient: EmailSendRecipient;
  onOpen: (recipient: EmailSendRecipient) => void;
}) {
  const meta = RECIPIENT_META[recipient.status as RecipientStatus];

  return (
    <tr className="border-line border-t">
      <td className="px-3 py-2">
        {recipient.hasContentSnapshot ? (
          <button
            type="button"
            onClick={() => onOpen(recipient)}
            title={`See exactly what was sent to ${recipient.email}`}
            className="group hover:text-accent text-ink-soft flex w-full max-w-56 items-center gap-1.5 text-left text-[12px]"
          >
            <Eye size={12} className="text-muted group-hover:text-accent shrink-0" />
            <span className="truncate">{recipient.email}</span>
          </button>
        ) : (
          <span className="text-ink-soft block max-w-56 truncate text-[12px]">
            {recipient.email}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <span
          className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
            meta?.className ?? 'badge-archived'
          }`}
        >
          {meta?.label ?? recipient.status}
        </span>
      </td>
      <td className="text-muted hidden max-w-64 truncate px-3 py-2 text-[11.5px] sm:table-cell">
        {recipient.errorMessage
          ? recipient.errorMessage
          : recipient.openedAt
            ? `Opened ${formatWhen(recipient.openedAt)}`
            : recipient.clickedAt
              ? `Clicked ${formatWhen(recipient.clickedAt)}`
              : '—'}
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────
// The copy one recipient was sent
//
// The sender job keeps the rendered HTML it handed to SMTP for each delivered
// recipient — that recipient's merge values and opt-out link already applied —
// so this shows the mail itself rather than a re-render of the document as it
// stands now. The two can differ: the email may have been edited, or even
// deleted, since.
//
// What the snapshot is not: a self-contained mail. Any image sent as an
// attachment is a `cid:` reference in this HTML, and the attachment travelled
// with the mail rather than being kept, so it renders broken in the frame.
// ─────────────────────────────────────────────────────────────────
export function RecipientCopyModal({
  sendId,
  subject,
  recipient,
  onClose,
}: {
  sendId: string;
  /** The email's subject as it reads now — see the footnote under the frame. */
  subject: string;
  recipient: EmailSendRecipient;
  onClose: () => void;
}) {
  // What was sent cannot change, so it is fetched once and never re-read.
  const copyQuery = useQuery({
    queryKey: ['email-sends', sendId, 'recipients', recipient.id, 'content'],
    queryFn: () => api.emailSends.recipientContent(sendId, recipient.id),
    staleTime: Infinity,
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const html = copyQuery.data?.contentSnapshot ?? null;
  const meta = RECIPIENT_META[recipient.status as RecipientStatus];

  const openInTab = () => {
    if (!html) return;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  return (
    <div className="bg-bg fixed inset-0 z-[70] flex flex-col">
      <header className="border-line bg-bg-soft/80 flex h-14 shrink-0 items-center gap-2.5 border-b px-3 backdrop-blur-md sm:px-5">
        <span className="bg-accent/10 text-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
          <MailOpen size={15} />
        </span>
        <div className="min-w-0">
          <p className="text-ink truncate text-[13px] font-bold tracking-tight">
            What was sent to {recipient.email}
          </p>
          <p className="text-muted flex items-center gap-1.5 truncate text-[11px]">
            <span className="truncate">{subject || 'Untitled email'}</span>
            <span aria-hidden="true">·</span>
            <span
              className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                meta?.className ?? 'badge-archived'
              }`}
            >
              {meta?.label ?? recipient.status}
            </span>
          </p>
        </div>

        {html && (
          <button
            type="button"
            onClick={openInTab}
            title="Open in a new tab"
            className="btn-ghost ml-auto rounded-lg px-2.5 py-1.5 text-xs"
          >
            <ExternalLink size={13} />
            <span className="hidden sm:inline">Open</span>
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Close"
          aria-label="Close the sent copy"
          className={`btn-ghost h-8 w-8 rounded-lg px-0 ${html ? '' : 'ml-auto'}`}
        >
          <X size={15} />
        </button>
      </header>

      <div className="relative min-h-0 flex-1 overflow-auto">
        {copyQuery.isLoading && (
          <div className="text-muted flex h-full items-center justify-center gap-2.5 text-sm">
            <Loader2 size={16} className="text-accent animate-spin" />
            Fetching the copy that went out…
          </div>
        )}

        {copyQuery.isError && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="bg-danger/10 text-danger flex h-12 w-12 items-center justify-center rounded-2xl">
              <TriangleAlert size={22} />
            </span>
            <p className="text-ink text-sm font-semibold">Couldn't load the copy</p>
            <p className="text-muted max-w-sm text-xs leading-relaxed">
              {copyQuery.error instanceof Error ? copyQuery.error.message : 'The request failed.'}
            </p>
            <button
              type="button"
              onClick={() => void copyQuery.refetch()}
              className="btn-secondary mt-2 text-xs"
            >
              Try again
            </button>
          </div>
        )}

        {copyQuery.isSuccess && !html && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <Mail size={20} className="text-muted" />
            <p className="text-ink text-[13px] font-semibold">No copy was kept</p>
            <p className="text-muted max-w-sm text-[12px]">
              {recipient.email} has no delivered copy on this send — the job keeps one per recipient
              only once it has actually gone out.
            </p>
          </div>
        )}

        {html && (
          <div className="flex min-h-full w-full justify-center px-2 py-4 sm:px-6">
            <div className="flex w-full flex-col items-center">
              <div className="border-line bg-bg-soft shadow-lift w-full max-w-[760px] overflow-hidden rounded-xl border">
                {/*
                  The delivered copy is inert, so the frame is locked down
                  harder than the editor's Preview: no scripts, and no
                  same-origin access, so nothing in a sent email can reach this
                  app's storage or navigate it.
                */}
                <iframe
                  title={`Copy sent to ${recipient.email}`}
                  srcDoc={html}
                  sandbox=""
                  className="bg-bg-soft h-[calc(100dvh_-_6.5rem)] w-full border-0"
                />
              </div>
              <p className="text-muted mt-2 max-w-[760px] pb-1 text-center text-[11px]">
                The exact HTML handed to the mail server for this recipient, with their merge
                values and opt-out link already applied. An image that travelled as an attachment
                shows as broken here — the bytes went with the mail and were not kept. The subject
                above is read from the email's current record.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
